import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Server, ArrowRight, Cloud, MapPin, RefreshCw, ExternalLink, Copy, Check } from 'lucide-react';
import { getPortalApiBaseUrl } from '../config/runtimeConfig';

type SelectTab = 'accounts' | 'versions';

type StreamManagerTarget = {
  id: string;
  name?: string;
  url: string;
  red5XDNId?: number;
  userId?: number;
  tag?: string;
  status?: string;
  created?: string;
  versionId?: number;
  ngcCount?: number;
  email?: string;
  last_login?: string;
  plan?: string;
  license_name?: string;
  region_code?: string;
  cloudProvider?: string;
};

type VersionRow = {
  id: number;
  name: string;
  streamManagerImageName?: string;
  kafkaImageName?: string;
  nodeImageName?: string;
  active?: number;
  created?: string;
  streamManagerImageNameORD?: string;
  nodeImageNameORD?: string;
};

type SortKey =
  | 'userId'
  | 'tag'
  | 'ngcCount'
  | 'url'
  | 'status'
  | 'versionId'
  | 'created'
  | 'email'
  | 'last_login'
  | 'plan'
  | 'license_name'
  | 'region_code'
  | 'cloudProvider';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function parseTargets(payload: unknown): StreamManagerTarget[] {
  if (!Array.isArray(payload)) return [];
  return payload
    .map((raw): StreamManagerTarget | null => {
      if (!isRecord(raw)) return null;
      const id = typeof raw.id === 'string' ? raw.id : '';
      const url =
        typeof raw.url === 'string'
          ? raw.url
          : typeof raw.baseUrl === 'string'
            ? raw.baseUrl
            : typeof raw.serverUrl === 'string'
              ? raw.serverUrl
              : '';
      if (!id || !url) return null;
      const name = typeof raw.name === 'string' ? raw.name : undefined;
      const red5XDNId = typeof raw.red5XDNId === 'number' ? raw.red5XDNId : undefined;
      const userId = typeof raw.userId === 'number' ? raw.userId : undefined;
      const tag = typeof raw.tag === 'string' ? raw.tag : undefined;
      const status = typeof raw.status === 'string' ? raw.status : undefined;
      const created = typeof raw.created === 'string' ? raw.created : undefined;
      const versionId = typeof raw.versionId === 'number' ? raw.versionId : undefined;
      const ngcCount = typeof raw.ngcCount === 'number' ? raw.ngcCount : undefined;
      const email = typeof raw.email === 'string' ? raw.email : undefined;
      const last_login = typeof raw.last_login === 'string' ? raw.last_login : undefined;
      const plan = typeof raw.plan === 'string' ? raw.plan : undefined;
      const license_name = typeof raw.license_name === 'string' ? raw.license_name : undefined;
      const region_code = typeof raw.region_code === 'string' ? raw.region_code : undefined;
      const cloudProvider = typeof raw.cloudProvider === 'string' ? raw.cloudProvider : undefined;
      return { id, name, url, red5XDNId, userId, tag, status, created, versionId, ngcCount, email, last_login, plan, license_name, region_code, cloudProvider };
    })
    .filter((t): t is StreamManagerTarget => t !== null);
}

function parseVersions(payload: unknown): VersionRow[] {
  if (!Array.isArray(payload)) return [];
  return payload
    .map((raw): VersionRow | null => {
      if (!isRecord(raw)) return null;
      const id = typeof raw.id === 'number' ? raw.id : -1;
      const name = typeof raw.name === 'string' ? raw.name : '';
      if (id <= 0 || !name) return null;
      const streamManagerImageName = typeof raw.streamManagerImageName === 'string' ? raw.streamManagerImageName : undefined;
      const kafkaImageName = typeof raw.kafkaImageName === 'string' ? raw.kafkaImageName : undefined;
      const nodeImageName = typeof raw.nodeImageName === 'string' ? raw.nodeImageName : undefined;
      const active = typeof raw.active === 'number' ? raw.active : undefined;
      const created = typeof raw.created === 'string' ? raw.created : undefined;
      const streamManagerImageNameORD = typeof raw.streamManagerImageNameORD === 'string' ? raw.streamManagerImageNameORD : undefined;
      const nodeImageNameORD = typeof raw.nodeImageNameORD === 'string' ? raw.nodeImageNameORD : undefined;
      return {
        id,
        name,
        streamManagerImageName,
        kafkaImageName,
        nodeImageName,
        active,
        created,
        streamManagerImageNameORD,
        nodeImageNameORD,
      };
    })
    .filter((v): v is VersionRow => v !== null);
}

const OCI_REGIONS = [
  'us-ashburn-1',
  'us-sanjose-1',
  'ap-sydney-1',
  'sa-saopaulo-1',
  'ca-toronto-1',
  'eu-milan-1',
  'mx-queretaro-1',
  'me-jeddah-1',
  'ap-singapore-1',
  'uk-cardiff-1',
] as const;

const ORD_REGIONS = ['ord16', 'ord17', 'ord18'] as const;

const SelectStreamManager: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SelectTab>('accounts');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [targets, setTargets] = useState<StreamManagerTarget[]>([]);
  const [query, setQuery] = useState('');
  const [selectingId, setSelectingId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [copyState, setCopyState] = useState<{ kind: 'user' | 'pass'; id: string } | null>(null);

  const portalApiBaseUrl = getPortalApiBaseUrl();
  const listUrlBase = portalApiBaseUrl ? `${portalApiBaseUrl}/portal/api/targets` : '/portal/api/targets';
  const listUrl = useMemo(() => {
    // Avoid truncating the "all statuses" view (default backend limit may be too low).
    const params = new URLSearchParams();
    params.set('limit', showAllStatuses ? '2000' : '1000');
    if (showAllStatuses) params.set('status', 'all');
    const qs = params.toString();
    return qs ? `${listUrlBase}?${qs}` : listUrlBase;
  }, [listUrlBase, showAllStatuses]);
  const versionsUrlBase = portalApiBaseUrl ? `${portalApiBaseUrl}/portal/api/versions` : '/portal/api/versions';
  const credentialsUrlFor = (id: string) =>
    portalApiBaseUrl
      ? `${portalApiBaseUrl}/portal/api/targets/${encodeURIComponent(id)}/credentials`
      : `/portal/api/targets/${encodeURIComponent(id)}/credentials`;
  const ngcListUrlFor = (id: string) =>
    portalApiBaseUrl
      ? `${portalApiBaseUrl}/portal/api/targets/${encodeURIComponent(id)}/node-group-configs`
      : `/portal/api/targets/${encodeURIComponent(id)}/node-group-configs`;
  const ngcJsonUrlFor = (id: number) =>
    portalApiBaseUrl
      ? `${portalApiBaseUrl}/portal/api/node-group-configs/${encodeURIComponent(String(id))}`
      : `/portal/api/node-group-configs/${encodeURIComponent(String(id))}`;

  type NgcListItem = { id: number; name: string; createdAt: string | undefined; updatedAt: string | undefined };
  const [ngcModalTargetId, setNgcModalTargetId] = useState<string | null>(null);
  const [ngcItemsByTargetId, setNgcItemsByTargetId] = useState<Record<string, NgcListItem[]>>({});
  const [ngcLoadingByTargetId, setNgcLoadingByTargetId] = useState<Record<string, boolean>>({});
  const [ngcErrorByTargetId, setNgcErrorByTargetId] = useState<Record<string, string>>({});

  const [ngcJsonModal, setNgcJsonModal] = useState<{ id: number; name: string } | null>(null);
  const [ngcJsonById, setNgcJsonById] = useState<Record<number, string>>({});
  const [ngcJsonLoadingById, setNgcJsonLoadingById] = useState<Record<number, boolean>>({});
  const [ngcJsonErrorById, setNgcJsonErrorById] = useState<Record<number, string>>({});
  const [ngcJsonCopied, setNgcJsonCopied] = useState(false);

  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string>('');
  const [versions, setVersions] = useState<VersionRow[]>([]);
  const [versionsQuery, setVersionsQuery] = useState('');
  const [versionsSortKey, setVersionsSortKey] = useState<'created' | 'id' | 'name' | 'active'>('created');
  const [versionsSortDir, setVersionsSortDir] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    const controller = new AbortController();

    if (activeTab !== 'accounts') return () => controller.abort();

    setLoading(true);
    setError('');
    fetch(listUrl, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) {
          throw new Error(`Failed to load targets (${res.status})`);
        }
        return res.json();
      })
      .then((data) => {
        setTargets(parseTargets(data));
      })
      .catch((e: unknown) => {
        if (isRecord(e) && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load Stream Manager targets.');
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      controller.abort();
    };
  }, [activeTab, listUrl, refreshNonce]);

  useEffect(() => {
    const controller = new AbortController();
    if (activeTab !== 'versions') return () => controller.abort();

    setVersionsLoading(true);
    setVersionsError('');
    const url = `${versionsUrlBase}?limit=5000`;
    fetch(url, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to load versions (${res.status})`);
        return res.json();
      })
      .then((data) => {
        setVersions(parseVersions(data));
      })
      .catch((e: unknown) => {
        if (isRecord(e) && e.name === 'AbortError') return;
        setVersionsError(e instanceof Error ? e.message : 'Failed to load versions.');
      })
      .finally(() => {
        setVersionsLoading(false);
      });

    return () => controller.abort();
  }, [activeTab, refreshNonce, versionsUrlBase]);

  async function fetchCredentials(target: StreamManagerTarget): Promise<{ serverUrl: string; username: string; password: string }> {
    const res = await fetch(credentialsUrlFor(target.id), { cache: 'no-store' });
    if (!res.ok) throw new Error(`Failed to load credentials (${res.status})`);
    const data: unknown = await res.json();
    if (!isRecord(data)) throw new Error('Invalid credentials payload');
    const serverUrl = typeof data.serverUrl === 'string' ? data.serverUrl : target.url;
    const username = typeof data.username === 'string' ? data.username : '';
    const password = typeof data.password === 'string' ? data.password : '';
    return { serverUrl, username, password };
  }

  async function handleUseTarget(target: StreamManagerTarget) {
    if (!target.id) return;
    setSelectingId(target.id);
    setError('');
    try {
      const { serverUrl, username, password } = await fetchCredentials(target);
      navigate('/login', { state: { serverUrl, username, password } });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load credentials.');
    } finally {
      setSelectingId(null);
    }
  }

  async function handleCopyCredential(target: StreamManagerTarget, kind: 'user' | 'pass') {
    if (!target.id) return;
    setCopyState({ kind, id: target.id });
    setError('');
    try {
      const { username, password } = await fetchCredentials(target);
      const value = kind === 'user' ? username : password;
      await navigator.clipboard.writeText(value);
      setTimeout(() => setCopyState(null), 1200);
    } catch (e: unknown) {
      setCopyState(null);
      setError(e instanceof Error ? e.message : 'Failed to copy credential.');
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return targets;
    return targets.filter((t) => {
      const haystack = `${t.name || ''} ${t.url} ${t.tag || ''} ${t.userId ?? ''} ${t.versionId ?? ''} ${t.ngcCount ?? ''} ${t.email || ''} ${t.plan || ''} ${t.license_name || ''} ${t.region_code || ''} ${t.cloudProvider || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [query, targets]);

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const getDateTs = (value: string | undefined): number => {
      if (!value) return -1;
      const ts = Date.parse(value);
      return Number.isFinite(ts) ? ts : -1;
    };

    const getValue = (t: StreamManagerTarget, key: SortKey): string | number => {
      switch (key) {
        case 'userId':
          return t.userId ?? -1;
        case 'ngcCount':
          return t.ngcCount ?? 0;
        case 'versionId':
          return t.versionId ?? -1;
        case 'created':
          return getDateTs(t.created);
        case 'last_login':
          return getDateTs(t.last_login);
        case 'tag':
          return (t.tag || t.id || '').toLowerCase();
        case 'url':
          return (t.url || '').toLowerCase();
        case 'status':
          return (t.status || '').toLowerCase();
        case 'email':
          return (t.email || '').toLowerCase();
        case 'plan':
          return (t.plan || '').toLowerCase();
        case 'license_name':
          return (t.license_name || '').toLowerCase();
        case 'region_code':
          return (t.region_code || '').toLowerCase();
        case 'cloudProvider':
          return (t.cloudProvider || '').toLowerCase();
        default:
          return (t.tag || t.id || '').toLowerCase();
      }
    };

    const tieBreaker = (t: StreamManagerTarget): string => (t.tag || t.id || '').toLowerCase();

    return [...filtered].sort((a, b) => {
      const va = getValue(a, sortKey);
      const vb = getValue(b, sortKey);
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') {
        cmp = va - vb;
      } else {
        cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      }
      if (cmp !== 0) return dir * cmp;
      return tieBreaker(a).localeCompare(tieBreaker(b), undefined, { numeric: true });
    });
  }, [filtered, sortDir, sortKey]);

  const filteredVersions = useMemo(() => {
    const q = versionsQuery.trim().toLowerCase();
    if (!q) return versions;
    return versions.filter((v) => {
      const haystack = `${v.id} ${v.name} ${v.streamManagerImageName || ''} ${v.kafkaImageName || ''} ${v.nodeImageName || ''} ${v.streamManagerImageNameORD || ''} ${v.nodeImageNameORD || ''}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [versions, versionsQuery]);

  const sortedVersions = useMemo(() => {
    const dir = versionsSortDir === 'asc' ? 1 : -1;
    const getCreatedTs = (value: string | undefined): number => {
      if (!value) return -1;
      const ts = Date.parse(value);
      return Number.isFinite(ts) ? ts : -1;
    };
    const getValue = (v: VersionRow): string | number => {
      switch (versionsSortKey) {
        case 'id':
          return v.id;
        case 'name':
          return v.name.toLowerCase();
        case 'active':
          return v.active ?? 0;
        case 'created':
        default:
          return getCreatedTs(v.created);
      }
    };
    return [...filteredVersions].sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);
      let cmp = 0;
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      if (cmp !== 0) return dir * cmp;
      return a.id - b.id;
    });
  }, [filteredVersions, versionsSortDir, versionsSortKey]);

  const providerCounts = useMemo(() => {
    const total = targets.length;
    let oci = 0;
    let openstack = 0;
    const ociByRegion: Record<string, number> = Object.fromEntries(OCI_REGIONS.map((r) => [r, 0]));
    const ordByRegion: Record<string, number> = Object.fromEntries(ORD_REGIONS.map((r) => [r, 0]));
    targets.forEach((t) => {
      const p = (t.cloudProvider || '').toUpperCase();
      const region = (t.region_code || '').toLowerCase();
      if (p === 'OCI') oci += 1;
      if (p === 'OPENSTACK') openstack += 1;
      if (p === 'OCI' && region && Object.prototype.hasOwnProperty.call(ociByRegion, region)) {
        ociByRegion[region] += 1;
      }
      if (region && Object.prototype.hasOwnProperty.call(ordByRegion, region)) {
        ordByRegion[region] += 1;
      }
    });
    return { total, oci, openstack, ociByRegion, ordByRegion };
  }, [targets]);

  const visibleOciRegions = useMemo(
    () => OCI_REGIONS.filter((r) => (providerCounts.ociByRegion[r] ?? 0) > 0),
    [providerCounts.ociByRegion]
  );
  const visibleOrdRegions = useMemo(
    () => ORD_REGIONS.filter((r) => (providerCounts.ordByRegion[r] ?? 0) > 0),
    [providerCounts.ordByRegion]
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortableHeader = ({ colKey, label, align = 'left', width }: { colKey: SortKey; label: string; align?: 'left' | 'right'; width?: string }) => {
    const isActive = sortKey === colKey;
    const arrow = isActive ? (sortDir === 'asc' ? '▲' : '▼') : '↕';
    return (
      <th
        className={`px-2 py-1 text-${align} text-xs font-normal text-white uppercase tracking-wider cursor-pointer select-none hover:bg-red5-card-darker ${width || ''}`}
        onClick={() => handleSort(colKey)}
        title={`Sort by ${label}`}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          <span className={isActive ? 'opacity-90' : 'opacity-50'} aria-hidden>
            {arrow}
          </span>
        </span>
      </th>
    );
  };

  const formatDateTime = (value?: string): string => {
    if (!value) return '—';
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return value;
    return d.toLocaleString();
  };

  const getStatusClassName = (statusRaw?: string): string => {
    const s = (statusRaw || '').trim().toLowerCase();
    if (!s) return 'text-xs font-normal text-white/70';
    if (s === 'launched') return 'text-xs font-normal text-red5-green';
    if (s === 'suspended') return 'text-xs font-normal text-purple-400';
    if (s === 'cancelled' || s === 'canceled') return 'text-xs font-normal text-red-400';

    // Checking / in-progress type statuses
    if (s === 'sm_health_checking') return 'text-xs font-normal text-blue-400';
    if (s.includes('health_checking')) return 'text-xs font-normal text-blue-400';
    if (s.includes('checking')) return 'text-xs font-normal text-blue-400';
    if (s.includes('creating') || s.includes('applying') || s.includes('tear_down')) {
      return 'text-xs font-normal text-red5-gold-light';
    }

    // Error states
    if (s.endsWith('_error') || s.includes('error') || s.includes('failed') || s.includes('fail')) {
      return 'text-xs font-normal text-red-400';
    }

    // Fallback
    return 'text-xs font-normal text-white/70';
  };

  async function openNgcModal(target: StreamManagerTarget) {
    const id = target.id;
    setNgcModalTargetId(id);
    if (ngcItemsByTargetId[id]) return;

    setNgcLoadingByTargetId((prev) => ({ ...prev, [id]: true }));
    setNgcErrorByTargetId((prev) => ({ ...prev, [id]: '' }));
    try {
      const res = await fetch(ngcListUrlFor(id));
      if (!res.ok) throw new Error(`Failed to load NGC list (${res.status})`);
      const data: unknown = await res.json();
      const items: NgcListItem[] = Array.isArray(data)
        ? data
            .map((raw) => {
              if (!isRecord(raw)) return null;
              const rid = typeof raw.id === 'number' ? raw.id : -1;
              const name = typeof raw.name === 'string' ? raw.name : '';
              if (rid <= 0 || !name) return null;
              const createdAt = typeof raw.createdAt === 'string' ? raw.createdAt : undefined;
              const updatedAt = typeof raw.updatedAt === 'string' ? raw.updatedAt : undefined;
              return { id: rid, name, createdAt, updatedAt };
            })
            .filter((x): x is NgcListItem => x !== null)
        : [];
      setNgcItemsByTargetId((prev) => ({ ...prev, [id]: items }));
    } catch (e: unknown) {
      setNgcErrorByTargetId((prev) => ({ ...prev, [id]: e instanceof Error ? e.message : 'Failed to load NGC list.' }));
    } finally {
      setNgcLoadingByTargetId((prev) => ({ ...prev, [id]: false }));
    }
  }

  async function openNgcJsonModal(item: NgcListItem) {
    setNgcJsonModal({ id: item.id, name: item.name });
    setNgcJsonCopied(false);
    if (ngcJsonById[item.id]) return;

    setNgcJsonLoadingById((prev) => ({ ...prev, [item.id]: true }));
    setNgcJsonErrorById((prev) => ({ ...prev, [item.id]: '' }));
    try {
      const res = await fetch(ngcJsonUrlFor(item.id));
      if (!res.ok) throw new Error(`Failed to load NGC JSON (${res.status})`);
      const data: unknown = await res.json();
      if (!isRecord(data)) throw new Error('Invalid NGC JSON payload');
      const rawJson = (data as Record<string, unknown>).nodeGroupConfigJsonData;
      const pretty = JSON.stringify(rawJson ?? null, null, 2);
      setNgcJsonById((prev) => ({ ...prev, [item.id]: pretty }));
    } catch (e: unknown) {
      setNgcJsonErrorById((prev) => ({ ...prev, [item.id]: e instanceof Error ? e.message : 'Failed to load NGC JSON.' }));
    } finally {
      setNgcJsonLoadingById((prev) => ({ ...prev, [item.id]: false }));
    }
  }

  const refreshTargets = () => {
    // Clear cached derived data so subsequent opens reflect latest DB state.
    setNgcModalTargetId(null);
    setNgcItemsByTargetId({});
    setNgcLoadingByTargetId({});
    setNgcErrorByTargetId({});
    setNgcJsonModal(null);
    setNgcJsonById({});
    setNgcJsonLoadingById({});
    setNgcJsonErrorById({});
    setNgcJsonCopied(false);
    setVersions([]);
    setVersionsError('');
    setRefreshNonce((n) => n + 1);
  };

  return (
    <div className="min-h-screen bg-red5-dark p-4 sm:p-6">
      <div className="w-full">
        <div className="bg-red5-gradient border border-red5-border rounded-lg shadow-xl p-6 sm:p-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h2 className="text-2xl font-normal text-white flex items-center gap-2">
                <Server className="h-5 w-5 text-red5-red shrink-0" />
                Select Stream Manager
              </h2>
              <p className="mt-1 text-sm text-white/80">
                Pick a Stream Manager URL to prefill the login form.
              </p>
            </div>

            <div className="shrink-0 flex items-center gap-2">
              <button
                type="button"
                onClick={refreshTargets}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-[8px] border border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover transition-colors text-sm font-normal disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh Stream Managers"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-[8px] border border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover transition-colors text-sm font-normal"
              >
                Go to Login
                <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2 border-b border-red5-border pb-3">
            <button
              type="button"
              onClick={() => setActiveTab('accounts')}
              className={`px-3 py-1.5 rounded-[8px] border text-sm font-normal transition-colors ${
                activeTab === 'accounts'
                  ? 'border-red5-red bg-red5-card-dark text-white'
                  : 'border-red5-border bg-transparent text-white/70 hover:bg-red5-card-hover'
              }`}
            >
              Accounts
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('versions')}
              className={`px-3 py-1.5 rounded-[8px] border text-sm font-normal transition-colors ${
                activeTab === 'versions'
                  ? 'border-red5-red bg-red5-card-dark text-white'
                  : 'border-red5-border bg-transparent text-white/70 hover:bg-red5-card-hover'
              }`}
            >
              Versions
            </button>
          </div>

          {activeTab === 'accounts' ? (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-white/70" />
                </div>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="pl-9 block w-full px-3 py-2 bg-red5-card-dark border border-red5-border rounded-md shadow-sm text-white placeholder-white placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-red5-red focus:border-red5-red text-sm"
                  placeholder="Search accounts…"
                />
              </div>
              <label className="mt-3 inline-flex items-center gap-2 text-xs text-white/80 select-none">
                <input
                  type="checkbox"
                  checked={showAllStatuses}
                  onChange={(e) => setShowAllStatuses(e.target.checked)}
                  className="h-4 w-4 rounded border border-red5-border bg-red5-card-dark text-red5-red focus:ring-red5-red"
                />
                Show all customers (all statuses)
              </label>
            </div>
          ) : (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-white/70" />
                </div>
                <input
                  value={versionsQuery}
                  onChange={(e) => setVersionsQuery(e.target.value)}
                  className="pl-9 block w-full px-3 py-2 bg-red5-card-dark border border-red5-border rounded-md shadow-sm text-white placeholder-white placeholder-opacity-50 focus:outline-none focus:ring-2 focus:ring-red5-red focus:border-red5-red text-sm"
                  placeholder="Search versions…"
                />
              </div>
            </div>
          )}

          {activeTab === 'accounts' && (
          <div className="mt-4 flex flex-nowrap items-stretch justify-start gap-4 border-t border-red5-border pt-4 overflow-x-auto min-w-0">
            <div
              className="flex flex-col items-center justify-center text-center bg-gradient-to-br from-red5-card-dark to-red5-card-darker border border-red5-border rounded-xl py-3 px-5 min-w-[140px] flex-shrink-0 shadow-[0_0_20px_rgba(0,0,0,0.3),0_0_16px_rgba(96,165,250,0.15)]"
              title={showAllStatuses ? 'Total Stream Managers (all statuses)' : 'Total Stream Managers (launched)'}
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Server className="w-4 h-4 flex-shrink-0 text-blue-400" />
                <span className="text-xs text-white/80">{showAllStatuses ? 'Total (all)' : 'Total (launched)'}</span>
              </div>
              <div className="text-[1.25rem] font-bold text-blue-400">{providerCounts.total}</div>
            </div>

            <div
              className="flex flex-col items-center justify-center text-center bg-gradient-to-br from-red5-card-dark to-red5-card-darker border border-red5-border rounded-xl py-3 px-5 min-w-[120px] flex-shrink-0 shadow-[0_0_20px_rgba(0,0,0,0.3),0_0_16px_rgba(248,113,113,0.15)]"
              title="OCI Stream Managers"
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Cloud className="w-4 h-4 flex-shrink-0 text-red5-red" />
                <span className="text-xs text-white/80">OCI</span>
              </div>
              <div className="text-[1.25rem] font-bold text-red5-red">{providerCounts.oci}</div>
            </div>

            <div
              className="flex flex-col items-center justify-center text-center bg-gradient-to-br from-red5-card-dark to-red5-card-darker border border-red5-border rounded-xl py-3 px-5 min-w-[140px] flex-shrink-0 shadow-[0_0_20px_rgba(0,0,0,0.3),0_0_16px_rgba(96,165,250,0.15)]"
              title="Openstack Stream Managers"
            >
              <div className="flex items-center justify-center gap-2 mb-2">
                <Cloud className="w-4 h-4 flex-shrink-0 text-blue-400" />
                <span className="text-xs text-white/80">Openstack</span>
              </div>
              <div className="text-[1.25rem] font-bold text-blue-400">{providerCounts.openstack}</div>
            </div>

            {visibleOciRegions.length > 0 && (
              <>
                <div className="shrink-0 w-px self-stretch bg-red5-border" aria-hidden="true" />

                <div
                  className="flex flex-col items-stretch justify-center bg-gradient-to-br from-red5-card-dark to-red5-card-darker border border-red5-border rounded-xl py-3 px-4 min-w-[320px] flex-shrink-0 shadow-[0_0_20px_rgba(0,0,0,0.3),0_0_16px_rgba(248,113,113,0.12)]"
                  title="OCI regions"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 flex-shrink-0 text-red5-red" />
                    <span className="text-xs text-white/80">OCI regions</span>
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
                    {visibleOciRegions.map((region) => (
                      <div key={`oci-${region}`} className="flex items-center gap-2">
                        <span className="text-xs font-mono text-white/80" title={region}>
                          {region}
                        </span>
                        <span className="text-xs font-bold text-red5-red">
                          {providerCounts.ociByRegion[region] ?? 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {visibleOrdRegions.length > 0 && (
              <>
                <div className="shrink-0 w-px self-stretch bg-red5-border" aria-hidden="true" />

                <div
                  className="flex flex-col items-stretch justify-center bg-gradient-to-br from-red5-card-dark to-red5-card-darker border border-red5-border rounded-xl py-3 px-4 min-w-[220px] flex-shrink-0 shadow-[0_0_20px_rgba(0,0,0,0.3),0_0_16px_rgba(96,165,250,0.12)]"
                  title="ORD regions"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 flex-shrink-0 text-blue-400" />
                    <span className="text-xs text-white/80">ORD regions</span>
                  </div>
                  <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
                    {visibleOrdRegions.map((region) => (
                      <div key={`ord-${region}`} className="flex items-center gap-2">
                        <span className="text-xs font-mono text-white/80">{region}</span>
                        <span className="text-xs font-bold text-blue-400">
                          {providerCounts.ordByRegion[region] ?? 0}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          )}

          {error && (
            <div className="mt-4 bg-red5-card-dark border border-red5-red text-red5-red px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}
          {activeTab === 'versions' && versionsError && (
            <div className="mt-4 bg-red5-card-dark border border-red5-red text-red5-red px-4 py-3 rounded-md text-sm">
              {versionsError}
            </div>
          )}
        </div>

        {activeTab === 'accounts' ? (
        <div className="mt-4 border border-red5-border rounded-lg bg-red5-card-dark/40">
          {loading ? (
            <div className="p-4 text-white/80 text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-white/80 text-sm">
              {targets.length === 0 ? 'No targets found.' : 'No matches.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="min-w-[1900px] p-2">
                <table className="w-full border-separate border-spacing-y-1">
                  <thead>
                    <tr className="bg-red5-card-darker">
                      <SortableHeader colKey="userId" label="UID" width="w-[90px]" />
                      <SortableHeader colKey="tag" label="Tag" width="w-[320px]" />
                      <SortableHeader colKey="url" label="SM URL" width="w-[160px]" />
                      <th className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider w-[110px]">
                        SM User
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider w-[110px]">
                        SM Pass
                      </th>
                      <SortableHeader colKey="ngcCount" label="NGC" width="w-[120px]" />
                      <SortableHeader colKey="status" label="Status" width="w-[140px]" />
                      <SortableHeader colKey="cloudProvider" label="Cloud" width="w-[140px]" />
                      <SortableHeader colKey="region_code" label="Region" width="w-[120px]" />
                      <SortableHeader colKey="versionId" label="Version" width="w-[110px]" />
                      <SortableHeader colKey="email" label="Email" width="w-[260px]" />
                      <SortableHeader colKey="plan" label="Plan" width="w-[200px]" />
                      <SortableHeader colKey="license_name" label="License" width="w-[200px]" />
                      <SortableHeader colKey="created" label="Created" width="w-[230px]" />
                      <SortableHeader colKey="last_login" label="Last login" width="w-[230px]" />
                      <th className="px-2 py-1 text-right text-xs font-normal text-white uppercase tracking-wider w-[120px]">
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody className="text-xs">
                    {sorted.map((t) => (
                      <tr key={t.id} className="bg-red5-card-dark hover:bg-red5-card-darker transition-colors">
                        <td className="px-2 py-1 whitespace-nowrap text-white/80">
                          {typeof t.userId === 'number' ? t.userId : '—'}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <div className="font-mono text-white/90 truncate max-w-[320px]" title={t.tag || t.id}>
                            {t.tag || t.id}
                          </div>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <a
                            href={t.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-[6px] border border-red5-red bg-red5-red/10 text-red5-red hover:bg-red5-red/20 transition-colors text-xs font-normal"
                            title={t.url}
                            aria-label={`Open ${t.url} in new tab`}
                          >
                            Open
                            <ExternalLink className="h-3.5 w-3.5 text-red5-red" />
                          </a>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleCopyCredential(t, 'user')}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-[6px] border border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover transition-colors text-xs font-normal"
                            title="Copy SM username"
                          >
                            {copyState?.kind === 'user' && copyState.id === t.id ? (
                              <>
                                <Check className="h-3.5 w-3.5" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="h-3.5 w-3.5" />
                                Copy
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => handleCopyCredential(t, 'pass')}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-[6px] border border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover transition-colors text-xs font-normal"
                            title="Copy SM password"
                          >
                            {copyState?.kind === 'pass' && copyState.id === t.id ? (
                              <>
                                <Check className="h-3.5 w-3.5" />
                                Copied
                              </>
                            ) : (
                              <>
                                <Copy className="h-3.5 w-3.5" />
                                Copy
                              </>
                            )}
                          </button>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          {(t.ngcCount ?? 0) > 0 ? (
                            <button
                              type="button"
                              onClick={() => openNgcModal(t)}
                              className="text-xs text-red5-gold-light hover:underline"
                              title="Open node group configs"
                            >
                              {t.ngcCount}
                            </button>
                          ) : (
                            <span className="text-white/60">—</span>
                          )}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <span
                            className={getStatusClassName(t.status)}
                            title={t.status || ''}
                          >
                            {t.status || '—'}
                          </span>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <span
                            className={
                              (t.cloudProvider || '').toUpperCase() === 'OCI'
                                ? 'text-xs font-normal text-red5-red'
                                : (t.cloudProvider || '').toUpperCase() === 'OPENSTACK'
                                  ? 'text-xs font-normal text-blue-400'
                                  : 'text-xs font-normal text-white/80'
                            }
                          >
                            {t.cloudProvider || '—'}
                          </span>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap text-white/80">
                          {t.region_code || '—'}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap text-white/80">
                          {typeof t.versionId === 'number' ? t.versionId : '—'}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <div className="text-white/80 truncate max-w-[260px]" title={t.email || ''}>
                            <span className={t.email ? 'text-red5-green font-normal' : 'text-white/80 font-normal'}>
                              {t.email || '—'}
                            </span>
                          </div>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <div className="text-white/80 truncate max-w-[200px]" title={t.plan || ''}>
                            {t.plan || '—'}
                          </div>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <div className="text-white/80 truncate max-w-[200px]" title={t.license_name || ''}>
                            {t.license_name || '—'}
                          </div>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <div className="font-mono text-white/60 truncate max-w-[230px]" title={t.created || ''}>
                            {formatDateTime(t.created)}
                          </div>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <div className="font-mono text-white/60 truncate max-w-[230px]" title={t.last_login || ''}>
                            {formatDateTime(t.last_login)}
                          </div>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap text-right">
                          <button
                            type="button"
                            onClick={() => handleUseTarget(t)}
                            disabled={selectingId === t.id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-[6px] border border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover transition-colors text-xs font-normal disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {selectingId === t.id ? 'Connecting…' : 'Connect'}
                            <ArrowRight className="h-3.5 w-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        ) : (
        <div className="mt-4 border border-red5-border rounded-lg bg-red5-card-dark/40">
          {versionsLoading ? (
            <div className="p-4 text-white/80 text-sm">Loading…</div>
          ) : sortedVersions.length === 0 ? (
            <div className="p-4 text-white/80 text-sm">
              {versions.length === 0 ? 'No versions found.' : 'No matches.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <div className="p-2">
                <table className="w-max border-separate border-spacing-y-1">
                  <thead>
                    <tr className="bg-red5-card-darker">
                      <th
                        className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider cursor-pointer hover:bg-red5-card-darker"
                        onClick={() => {
                          if (versionsSortKey === 'id') setVersionsSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                          else {
                            setVersionsSortKey('id');
                            setVersionsSortDir('asc');
                          }
                        }}
                      >
                        ID
                      </th>
                      <th
                        className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider cursor-pointer hover:bg-red5-card-darker"
                        onClick={() => {
                          if (versionsSortKey === 'name') setVersionsSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                          else {
                            setVersionsSortKey('name');
                            setVersionsSortDir('asc');
                          }
                        }}
                      >
                        Name
                      </th>
                      <th
                        className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider cursor-pointer hover:bg-red5-card-darker"
                        onClick={() => {
                          if (versionsSortKey === 'active') setVersionsSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                          else {
                            setVersionsSortKey('active');
                            setVersionsSortDir('desc');
                          }
                        }}
                      >
                        Active
                      </th>
                      <th
                        className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider cursor-pointer hover:bg-red5-card-darker"
                        onClick={() => {
                          if (versionsSortKey === 'created') setVersionsSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                          else {
                            setVersionsSortKey('created');
                            setVersionsSortDir('desc');
                          }
                        }}
                      >
                        Created
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider">
                        SM image
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider">
                        Kafka image
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider">
                        Node image
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider">
                        SM image (ORD)
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider">
                        Node image (ORD)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-xs">
                    {sortedVersions.map((v) => (
                      <tr key={v.id} className="bg-red5-card-dark hover:bg-red5-card-darker transition-colors">
                        <td className="px-2 py-1 whitespace-nowrap text-white/80">{v.id}</td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <div className="font-mono text-white/90 whitespace-nowrap" title={v.name}>
                            {v.name}
                          </div>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap">
                          <span className={v.active ? 'text-red5-green' : 'text-white/60'}>{v.active ? '1' : '0'}</span>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap font-mono text-white/60" title={v.created || ''}>
                          {formatDateTime(v.created)}
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap font-mono text-white/70" title={v.streamManagerImageName || ''}>
                          <span className="whitespace-nowrap">{v.streamManagerImageName || '—'}</span>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap font-mono text-white/70" title={v.kafkaImageName || ''}>
                          <span className="whitespace-nowrap">{v.kafkaImageName || '—'}</span>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap font-mono text-white/70" title={v.nodeImageName || ''}>
                          <span className="whitespace-nowrap">{v.nodeImageName || '—'}</span>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap font-mono text-white/70" title={v.streamManagerImageNameORD || ''}>
                          <span className="whitespace-nowrap">{v.streamManagerImageNameORD || '—'}</span>
                        </td>
                        <td className="px-2 py-1 whitespace-nowrap font-mono text-white/70" title={v.nodeImageNameORD || ''}>
                          <span className="whitespace-nowrap">{v.nodeImageNameORD || '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        )}

        {ngcModalTargetId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setNgcModalTargetId(null)} />
            <div className="relative w-full max-w-2xl bg-red5-gradient border border-red5-border rounded-lg shadow-xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-red5-border">
                <div className="text-white font-normal">Node Group Configs</div>
                <button
                  type="button"
                  onClick={() => setNgcModalTargetId(null)}
                  className="text-white/80 hover:text-white text-sm"
                >
                  Close
                </button>
              </div>

              <div className="p-4">
                {ngcLoadingByTargetId[ngcModalTargetId] ? (
                  <div className="text-white/80 text-sm">Loading…</div>
                ) : ngcErrorByTargetId[ngcModalTargetId] ? (
                  <div className="bg-red5-card-dark border border-red5-red text-red5-red px-4 py-3 rounded-md text-sm">
                    {ngcErrorByTargetId[ngcModalTargetId]}
                  </div>
                ) : (ngcItemsByTargetId[ngcModalTargetId] || []).length === 0 ? (
                  <div className="text-white/70 text-sm">No node group configs found.</div>
                ) : (
                  <div className="border border-red5-border rounded-md overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-red5-card-darker">
                        <tr>
                          <th className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider">
                            Name
                          </th>
                          <th className="px-2 py-1 text-left text-xs font-normal text-white uppercase tracking-wider">
                            Updated
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-red5-card-dark text-xs">
                        {(ngcItemsByTargetId[ngcModalTargetId] || []).map((item) => (
                          <tr key={item.id} className="hover:bg-red5-card-darker transition-colors">
                            <td className="px-2 py-1">
                              <button
                                type="button"
                                onClick={() => openNgcJsonModal(item)}
                                className="text-red5-red hover:underline text-left"
                                title="Open JSON"
                              >
                                {item.name}
                              </button>
                            </td>
                            <td className="px-2 py-1 font-mono text-white/70">
                              {formatDateTime(item.updatedAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {ngcJsonModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60" onClick={() => setNgcJsonModal(null)} />
            <div className="relative w-full max-w-4xl bg-red5-gradient border border-red5-border rounded-lg shadow-xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-red5-border gap-3">
                <div className="text-white font-normal truncate" title={ngcJsonModal.name}>
                  NGC JSON: {ngcJsonModal.name}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      const json = ngcJsonById[ngcJsonModal.id];
                      if (!json) return;
                      navigator.clipboard.writeText(json).then(() => {
                        setNgcJsonCopied(true);
                        setTimeout(() => setNgcJsonCopied(false), 1500);
                      });
                    }}
                    disabled={!ngcJsonById[ngcJsonModal.id]}
                    className={`inline-flex items-center px-3 py-1.5 rounded-[6px] border text-xs transition-colors ${
                      ngcJsonCopied
                        ? 'border-red5-green bg-red5-green/20 text-red5-green'
                        : 'border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {ngcJsonCopied ? 'Copied' : 'Copy JSON'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNgcJsonModal(null)}
                    className="text-white/80 hover:text-white text-sm"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="p-4">
                {ngcJsonLoadingById[ngcJsonModal.id] ? (
                  <div className="text-white/80 text-sm">Loading…</div>
                ) : ngcJsonErrorById[ngcJsonModal.id] ? (
                  <div className="bg-red5-card-dark border border-red5-red text-red5-red px-4 py-3 rounded-md text-sm">
                    {ngcJsonErrorById[ngcJsonModal.id]}
                  </div>
                ) : (
                  <pre className="bg-red5-card-darker border border-red5-border rounded-md p-3 text-xs font-mono text-red5-green-light overflow-x-auto whitespace-pre-wrap break-all max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {ngcJsonById[ngcJsonModal.id] || ''}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 text-xs text-white/70">
          Selector API endpoint:{' '}
          <span className="font-mono">
            {portalApiBaseUrl ? `${portalApiBaseUrl}/portal/api/targets` : '/portal/api/targets'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default SelectStreamManager;

